import apiClient from "./apiClient";

/**
 * Servicio para manejar carritos temporales en el servidor
 */

// Obtiene el ID anónimo del almacenamiento local o genera uno nuevo
const getAnonymousId = async () => {
  let anonymousId = localStorage.getItem('cart_anonymous_id');
  
  if (!anonymousId) {
    try {
      // Si no hay ID anónimo, solicitar uno nuevo al servidor usando el endpoint público
      const response = await apiClient.post('/orders/public/generate-id/');
      anonymousId = response.data.anonymous_id;
      localStorage.setItem('cart_anonymous_id', anonymousId);
    } catch (error) {
      console.error('Error al generar ID anónimo:', error);
      // En caso de error, generar un ID localmente (no es ideal pero funciona como fallback)
      anonymousId = 'local_' + Date.now().toString() + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('cart_anonymous_id', anonymousId);
    }
  }
  
  return anonymousId;
};

// Enviar carrito al servidor
export const syncCartWithServer = async (cartItems) => {
  try {
    // No sincronizamos si no hay items
    if (!cartItems || cartItems.length === 0) {
      console.log('No hay items para sincronizar');
      return null;
    }
    
    // Verificar si el usuario está autenticado
    const isAuthenticated = !!localStorage.getItem('accessToken');
    
    // Si el usuario está autenticado, no usamos carrito temporal anónimo
    // (el carrito se maneja diferente para usuarios autenticados)
    if (isAuthenticated) {
      console.log('Usuario autenticado, no se usa carrito temporal');
      return null;
    }
    
    const anonymousId = await getAnonymousId();
    
    // Convertir formato de items para el servidor
    const serverItems = cartItems.map(item => ({
      product_id: item.id,
      quantity: item.quantity,
      variants: item.variants || {},
      variants_label: item.variants_label || ''
    }));
    
    // Enviar el carrito al servidor usando el endpoint público
    const response = await apiClient.post('/orders/public/temp-cart/', {
      anonymous_id: anonymousId,
      items: serverItems
    });
    
    // Si la respuesta contiene un nuevo anonymous_id, guardarlo
    if (response.data && response.data.anonymous_id) {
      localStorage.setItem('cart_anonymous_id', response.data.anonymous_id);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error al sincronizar carrito con el servidor:', error);
    // No lanzamos error para que el flujo del usuario no se interrumpa
    return null;
  }
};

// Recuperar carrito del servidor usando el ID anónimo
export const fetchCartFromServer = async () => {
  try {
    // Si el usuario está autenticado, usar el endpoint de usuario
    const isAuthenticated = !!localStorage.getItem('accessToken');
    
    if (isAuthenticated) {
      console.log('Usuario autenticado, obteniendo carrito de usuario');
      // Para usuarios autenticados, deberíamos usar un endpoint específico
      // Si no existe, podemos crear uno o extender la funcionalidad actual
      return null; // Por ahora retornamos null para no interferir con el carrito local
    }
    
    const anonymousId = localStorage.getItem('cart_anonymous_id');
    if (!anonymousId) return null;
    
    // Usar el endpoint público para obtener el carrito anónimo
    const response = await apiClient.get(`/orders/public/temp-cart/${anonymousId}/`);
    return response.data;
  } catch (error) {
    console.error('Error al recuperar carrito del servidor:', error);
    return null;
  }
};

// Recuperar la información del cliente desde un carrito temporal
export const fetchCustomerInfoFromTempCart = async () => {
  try {
    const anonymousId = localStorage.getItem('cart_anonymous_id');
    if (!anonymousId) return null;
    
    const response = await apiClient.get(`/orders/public/temp-cart/${anonymousId}/`);
    return response.data?.customer_info || null;
  } catch (error) {
    console.error('Error al recuperar información del cliente:', error);
    return null;
  }
};

// Función para convertir el carrito del servidor al formato local
export const convertServerCartToLocal = (serverCart) => {
  if (!serverCart || !serverCart.products) return [];
  
  return serverCart.products.map(item => ({
    id: item.product_details?.id,
    name: item.product_details?.name,
    price: parseFloat(item.price_at_time),
    image: item.product_details?.image,
    quantity: item.quantity,
    variants: item.variants || {},
    variants_label: item.variants_label || '',
    currency: item.product_details?.currency || 'ARS',
    has_special_promotion: item.product_details?.has_special_promotion || false,
    special_promotion_name: item.product_details?.special_promotion_name || null,
    lineId: `${item.product_details?.id}::${item.variants_label || ''}::${JSON.stringify(item.variants || {})}`
  }));
};